import { Component, ElementRef, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OpenService } from '@app-services/open.service';
import { SessionService } from '@app/store/session/session.service';
import { LayoutVars, UserInfo } from '@app/store/session/session.store';
import { MarketPlacePersonalizationUtils } from "@app-components/layouts/shopkeeper-layout/marketplace-personalization";
import { environment } from 'src/environments/environment';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminService } from '@app/views/private/admin/services/admin.service';
import { DealerDetails } from '@app/views/private/admin/model/admin-models';
import { StringUtils } from '@app/shared/utils/StringUtils';

@Component({
  selector: 'app-dialog-encouraging-migration',
  templateUrl: './dialog-encouraging-migration.component.html',
  styleUrls: ['./dialog-encouraging-migration.component.scss']
})
export class DialogEncouragingMigrationComponent implements OnInit {

  dealerDetails: DealerDetails;

  totalSteps = 3;
  selectedTabIndex = 0;

  layoutVars: LayoutVars
  layoutVarsResponse: boolean = false;
  currentStep = 0;
  userInfo: UserInfo;

  isMandatory = false;

  showIframe = false;
  sbxSafeUrl: SafeResourceUrl;
  callbackUrl = `${window.location.protocol}//${window.location.host}/authorization/`;
  private redirectUri = '/arrematar/home'
  isLoadingIframe = false;

  sbxPrefillData: any;
  sbxDetectCallback = { urlStartsWith: this.callbackUrl };

  mandatoryDate: string;

  constructor(
    public dialogRef: MatDialogRef<DialogEncouragingMigrationComponent>,
    private sessionService: SessionService,
    private openService: OpenService,
    private elRef: ElementRef,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    private adminService: AdminService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.openService.getLayoutVars()
      .subscribe(layoutVars => {
        this.layoutVars = layoutVars;
        this.layoutVarsResponse = true;
        MarketPlacePersonalizationUtils.initCssVars(this.elRef, layoutVars);
      });
    this.isMandatory = !!data?.mandatory;
    this.mandatoryDate = data?.mandatoryDate;
  }

  ngOnInit(): void {
    this.sessionService.getUser().subscribe(userInfo => {
      this.userInfo = userInfo;
      if (userInfo?.dealerId) {
        this.adminService.getDealer(userInfo.dealerId).subscribe(details => {
          this.dealerDetails = details;
        });
      }
    });
  }

  next(): void {
    if (this.currentStep < this.totalSteps - 1) {
      this.currentStep++;
    } else {
      if (this.isMandatory) {
        this.dialogRef.close({ completed: true });
      } else {
        this.dialogRef.close({ migrationStarted: true });
      }
    }
  }

  prev(): void {
    if (this.isMandatory && this.currentStep === 0) {
      this.router.navigate(['/logout']);
      this.dialogRef.close();
    } else {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    }
  }

  close(): void {
    if (!this.isMandatory) {
      this.dialogRef.close();
    }
  }

  redirectSuperbidAccounts({ redirectToSignup = false }): void {
    const base = environment.superBidUrl;
    const action = redirectToSignup ? 'signup' : 'signin';
    const finalUrl =
      `${base}/${action}?response_type=token&client_id=${environment.superBidClientId}` +
      `&redirect_uri=${encodeURIComponent(this.redirectUri)}` +
      `&authorization_uri=${encodeURIComponent(this.callbackUrl)}` +
      `&externalClient=true` +
      `&isFrame=true` +
      `&redirect_uri=${encodeURIComponent(this.redirectUri)}` +
      `&authorization_uri=${encodeURIComponent(this.callbackUrl)}` +
      `&language=pt-BR&portal_id=2` +
      `&hostName=Superbid%20BR` +
      `&source=auto_arremate`;

    this.sbxPrefillData = this.buildPrefillData();
    this.sbxSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(finalUrl);
    this.showIframe = true;
  }

  onMessage(event: MessageEvent): void {
    const t = event?.data?.type;
    if (t === 'IFRAME_READY') this.isLoadingIframe = true;
    if (t === 'FORM_FILLED' || t === 'DATA_RECEIVED') this.isLoadingIframe = false;
  }

  onFlowCompleted(): void {
    this.showIframe = false;
    if (this.userInfo?.isMigrationMandatory) {
      this.dialogRef.close({ completed: true });
      this.router.navigate(['/logout'])
    }
  }

  private buildPrefillData(): any {
    
    const u = this.userInfo;
    const d = this.dealerDetails;
    if (!u) return null;

    const phoneSrc = d?.phoneNumbers && d.phoneNumbers.length > 0 ? d.phoneNumbers[0] : d?.whatsappNumber;
    const phone = StringUtils.parsePhoneBr(phoneSrc);

    const cpfCnpj = StringUtils.toDigits(d?.cpfCnpj);
    const isCompany = (d?.type === 'COMPANY') || (cpfCnpj && cpfCnpj.length > 11);
    
    const repType = isCompany ? (this.selectedTabIndex === 1 ? 2 : 1) : undefined;

    const { text: civilText, code: civilCode } = StringUtils.normalizeCivilState(d?.maritalStatus);

    const payload: any = {
      email: u.email,
      name: u.name || d?.legalName || d?.name,
      fullName: u.name,
      nickname: u.firstName,
      cep: StringUtils.maskZip(d?.zipCode),
      state: d?.uf,
      city: d?.cityName,
      address: d?.address,
      number: d?.addressNumber,
      district: d?.district,
      complement: d?.addressComplement,
      ...phone,
    };

    if (isCompany) {
      // Pessoa Jurídica
      const legalOrName = (d?.legalName && d.legalName.trim()) ? d.legalName : d?.name;
      Object.assign(payload, {
        cnpj: cpfCnpj,
        company: legalOrName,
        companyName: legalOrName,
        socialReason: legalOrName,
        fantasyName: d?.name,
        nomeFantasia: d?.name,
        companyRepresentativeTypeId: repType,
        representativeTypeId: repType,
        tipoRepresentante: repType,
        stateRegister: d?.stateRegistration,
        municipalRegister: d?.cityRegistration,
        civilState: civilCode || civilText,
      });
    } else {
      // Pessoa Física
      Object.assign(payload, {
        cpf: cpfCnpj,
        document: cpfCnpj,
        dateOfBirth: StringUtils.toIsoDateBrOrIso(d?.birthDate),
        civilState: civilCode || civilText,
      });
    }

    // Remove undefined
    Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
    return payload;
  }
}
